import { motion, type HTMLMotionProps, type Variants } from 'framer-motion';
import { forwardRef } from 'react';

// Common animation variants
export const fadeInUp: Variants = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -20 }
};

export const fadeInDown: Variants = {
	initial: { opacity: 0, y: -20 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: 20 }
};

export const fadeInLeft: Variants = {
	initial: { opacity: 0, x: -20 },
	animate: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: 20 }
};

export const fadeInRight: Variants = {
	initial: { opacity: 0, x: 20 },
	animate: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: -20 }
};

export const scaleIn: Variants = {
	initial: { opacity: 0, scale: 0.9 },
	animate: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.9 }
};

export const slideInFromBottom: Variants = {
	initial: { opacity: 0, y: 50 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: 50 }
};

export const staggerContainer: Variants = {
	initial: {},
	animate: {
		transition: {
			staggerChildren: 0.1
		}
	}
};

export const bounceIn: Variants = {
	initial: { opacity: 0, scale: 0.3 },
	animate: {
		opacity: 1,
		scale: 1,
		transition: {
			type: "spring",
			damping: 10,
			stiffness: 100
		}
	},
	exit: { opacity: 0, scale: 0.3 }
};

// Reusable Motion Components
export const MotionDiv = motion.div;
export const MotionButton = motion.button;
export const MotionSpan = motion.span;
export const MotionH1 = motion.h1;
export const MotionP = motion.p;

// Enhanced Button with motion
interface MotionButtonProps
	extends Omit<HTMLMotionProps<"button">, "whileHover" | "whileTap"> {
	whileHover?: any;
	whileTap?: any;
}

export const EnhancedMotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
	({ children, whileHover, whileTap, className, onClick, disabled, ...props }, ref) => {
		return (
			<motion.button
				ref={ref}
				whileHover={{
					scale: 1.02,
					y: -2,
					boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
					...whileHover
				}}
				whileTap={{
					scale: 0.98,
					y: 0,
					...whileTap
				}}
				transition={{
					type: "spring",
					stiffness: 400,
					damping: 17
				}}
				className={className}
				onClick={onClick}
				disabled={disabled}
				{...props}
			>
				{children}
			</motion.button>
		);
	}
);

EnhancedMotionButton.displayName = "EnhancedMotionButton";

// Page transition wrapper
interface PageTransitionProps {
	children: React.ReactNode;
	className?: string;
}

export const PageTransition: React.FC<PageTransitionProps> = ({ children, className }) => {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			transition={{
				duration: 0.3,
				ease: "easeOut"
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
};

// Stagger children animation wrapper
export const StaggerChildren: React.FC<PageTransitionProps> = ({ children, className }) => {
	return (
		<motion.div
			variants={staggerContainer}
			initial="initial"
			animate="animate"
			className={className}
		>
			{children}
		</motion.div>
	);
};

// Loading spinner with motion
export const MotionLoader: React.FC<{ className?: string }> = ({ className }) => {
	return (
		<motion.div
			animate={{ rotate: 360 }}
			transition={{
				duration: 1,
				repeat: Infinity,
				ease: "linear"
			}}
			className={className}
		>
			<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
				<circle
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					strokeWidth="4"
					strokeDasharray="32"
					strokeDashoffset="32"
				>
					<animate
						attributeName="stroke-dasharray"
						dur="2s"
						values="0 32;16 16;0 32;0 32"
						repeatCount="indefinite"
					/>
					<animate
						attributeName="stroke-dashoffset"
						dur="2s"
						values="0;-16;-32;-32"
						repeatCount="indefinite"
					/>
				</circle>
			</svg>
		</motion.div>
	);
};

// Number counter animation
interface AnimatedNumberProps {
	value: number;
	className?: string;
	prefix?: string;
	suffix?: string;
}

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
	value,
	className,
	prefix = "",
	suffix = ""
}) => {
	return (
		<motion.span
			key={value}
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			transition={{ duration: 0.3, ease: "easeOut" }}
			className={className}
		>
			{prefix}{value}{suffix}
		</motion.span>
	);
};

// Floating action button
export const FloatingButton: React.FC<MotionButtonProps> = ({
	children,
	className,
	...props
}) => {
	return (
		<motion.button
			whileHover={{
				scale: 1.1,
				boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
			}}
			whileTap={{ scale: 0.95 }}
			transition={{
				scale: {
					type: "spring",
					stiffness: 400,
					damping: 17
				}
			}}
			className={className}
			{...props}
		>
			{children}
		</motion.button>
	);
};
